import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { OrganizationApi } from '../../services/ApiService';
import { OrgAdminApi } from '../../services/api/OrgAdminApi';

const ROLE_OPTIONS = [
  { value: 'org_member', label: 'Member' },
  { value: 'course_admin', label: 'Course Admin' },
];

function CreateOrganizationPage() {
  const { id, organizationId } = useParams();
  const navigate = useNavigate();
  const orgId = id || organizationId;
  const isEditing = Boolean(orgId);

  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Domain management (edit mode only)
  const [domains, setDomains] = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [showDomainForm, setShowDomainForm] = useState(false);
  const [domainForm, setDomainForm] = useState({ domain: '', autoRole: 'org_member' });
  const [domainError, setDomainError] = useState('');

  // Load org details when editing
  useEffect(() => {
    if (!isEditing) return;
    setLoading(true);
    OrganizationApi.getOrganization(orgId).then(async (resp) => {
      if (resp.ok) {
        const org = await resp.json();
        setName(org.name || org.organizationName || '');
      } else {
        setError('Failed to load organization');
      }
    }).catch(() => setError('Network error'))
      .finally(() => setLoading(false));
  }, [orgId, isEditing]);

  // Load domains when editing
  const loadDomains = useCallback(async () => {
    if (!isEditing) return;
    setDomainsLoading(true);
    try {
      const resp = await OrgAdminApi.getEmailDomains(orgId);
      if (resp.ok) setDomains(await resp.json());
    } catch (e) { /* ignore */ }
    finally { setDomainsLoading(false); }
  }, [orgId, isEditing]);

  useEffect(() => { loadDomains(); }, [loadDomains]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (!name.trim()) { setError('Organization name is required'); return; }

    try {
      let resp;
      if (isEditing) {
        resp = await OrganizationApi.updateOrganization(orgId, { name: name.trim() });
      } else {
        resp = await OrganizationApi.createOrganization({ name: name.trim() });
      }

      if (!resp.ok) {
        const body = await resp.text();
        setError(body || `Failed to ${isEditing ? 'update' : 'create'} organization`);
        return;
      }

      if (isEditing) {
        setMessage('Organization updated successfully!');
      } else {
        const data = await resp.json();
        setMessage('Organization created successfully!');
        // Navigate to the edit page for the new org
        setTimeout(() => navigate(`/settings/organizations/${data.organizationId || data.organization_id || data.id}`), 800);
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    }
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    setDomainError('');
    if (!domainForm.domain.trim()) return;

    try {
      const resp = await OrgAdminApi.upsertEmailDomain(orgId, {
        domain: domainForm.domain.trim(),
        autoRole: domainForm.autoRole,
      });
      if (resp.status === 409) { setDomainError('This domain is already assigned to another organization.'); return; }
      if (!resp.ok) {
        const body = await resp.json().catch(() => null);
        setDomainError(body?.error || `Failed to add domain (${resp.status})`);
        return;
      }
      setDomainForm({ domain: '', autoRole: 'org_member' });
      setShowDomainForm(false);
      loadDomains();
    } catch (err) {
      setDomainError(err.message || 'Network error');
    }
  };

  const handleDeleteDomain = async (domainId, domainName) => {
    if (!window.confirm(`Remove domain "${domainName}" from this organization?`)) return;
    const resp = await OrgAdminApi.deleteEmailDomain(orgId, domainId);
    if (resp.ok || resp.status === 204) {
      loadDomains();
    } else {
      setDomainError('Failed to remove domain');
    }
  };

  if (loading) {
    return <div className="settings-page-content"><div className="loading-state"><div className="loading-spinner" /><p>Loading…</p></div></div>;
  }

  return (
    <div className="settings-page-content">
      <div className="settings-page-header">
        <h2>
          <Link to="/settings/organizations" style={{ color: 'var(--text-muted)', textDecoration: 'none', fontSize: '0.9em' }}>
            Organizations
          </Link>
          {' › '}
          {isEditing ? 'Edit Organization' : 'Create Organization'}
        </h2>
      </div>

      {error && <div className="error-message">{error}</div>}
      {message && <div className="success-message" style={{ padding: '0.75rem 1rem', background: '#d4edda', color: '#155724', borderRadius: '8px', marginBottom: '1rem' }}>{message}</div>}

      {/* Organization Name Form */}
      <div style={{ padding: '1.5rem', background: 'var(--card-bg, #fff)', border: '1px solid var(--border-color, #e1e5e9)', borderRadius: '12px', marginBottom: '1.5rem' }}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Organization Details</h3>
        <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '4px' }}>Name</label>
            <input
              type="text"
              placeholder="e.g. Springfield University"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              style={{ width: '100%' }}
            />
          </div>
          <button type="submit">{isEditing ? 'Save Changes' : 'Create Organization'}</button>
        </form>
      </div>

      {/* Email Domains Section (edit mode only) */}
      {isEditing && (
        <div style={{ padding: '1.5rem', background: 'var(--card-bg, #fff)', border: '1px solid var(--border-color, #e1e5e9)', borderRadius: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0 }}>Email Domains</h3>
            <button type="button" onClick={() => setShowDomainForm(f => !f)}>
              {showDomainForm ? 'Cancel' : '+ Add Domain'}
            </button>
          </div>

          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: 0, marginBottom: '1rem' }}>
            Users who sign up with an email matching one of these domains will be automatically enrolled into this organization with the configured role.
          </p>

          {domainError && <div className="error-message">{domainError}</div>}

          {showDomainForm && (
            <form onSubmit={handleAddDomain} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', marginBottom: '1rem', padding: '1rem', background: 'var(--light-color, #f8f9fa)', borderRadius: '8px', border: '1px solid var(--border-color, #e1e5e9)' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '4px' }}>Domain</label>
                <input
                  type="text"
                  placeholder="e.g. university.edu"
                  value={domainForm.domain}
                  onChange={(e) => setDomainForm(f => ({ ...f, domain: e.target.value }))}
                  required
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '4px' }}>Auto-Assign Role</label>
                <select value={domainForm.autoRole} onChange={(e) => setDomainForm(f => ({ ...f, autoRole: e.target.value }))}>
                  {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <button type="submit">Add</button>
            </form>
          )}

          {domainsLoading ? (
            <div className="loading-state"><div className="loading-spinner" /><p>Loading domains…</p></div>
          ) : domains.length === 0 ? (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)', background: 'var(--light-color, #f8f9fa)', borderRadius: '8px' }}>
              <p style={{ margin: 0 }}>No email domains configured. Add one to enable auto-enrollment.</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Auto-Assign Role</th>
                  <th>Verified</th>
                  <th>Added</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {domains.map(d => (
                  <tr key={d.id}>
                    <td data-label="Domain"><strong>{d.domain}</strong></td>
                    <td data-label="Role">
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 600,
                        background: d.autoRole === 'course_admin' ? '#dbeafe' : '#e5e7eb',
                        color: d.autoRole === 'course_admin' ? '#1e40af' : '#374151',
                      }}>
                        {ROLE_OPTIONS.find(r => r.value === d.autoRole)?.label || d.autoRole}
                      </span>
                    </td>
                    <td data-label="Verified">
                      <span style={{ color: d.verified ? '#16a34a' : '#9ca3af' }}>
                        {d.verified ? '✓ Verified' : '○ Unverified'}
                      </span>
                    </td>
                    <td data-label="Added">
                      {d.createdAt ? new Date(d.createdAt).toLocaleDateString() : '—'}
                    </td>
                    <td data-label="Actions">
                      <button
                        type="button"
                        onClick={() => handleDeleteDomain(d.id, d.domain)}
                        style={{ background: 'transparent', color: '#dc2626', border: 'none', cursor: 'pointer', fontWeight: 600, padding: '4px 8px' }}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Info panel */}
      <div className="info-panel" style={{ marginTop: '1.5rem' }}>
        <h4>About Email Domains</h4>
        <p>
          Email domains control automatic enrollment. When a new user signs up with an email address matching a configured domain
          (e.g. <code>@university.edu</code>), they are automatically added to this organization with the specified role.
          This avoids manual invitations for large groups. Note: <code>org_admin</code> cannot be auto-assigned for security reasons — org admins must be promoted explicitly.
        </p>
      </div>
    </div>
  );
}

export default CreateOrganizationPage;
